/**
 * FERP Portal - Condition Evaluator
 *
 * Evaluates conditions for field visibility, validation, and step navigation.
 * Supports simple conditions, compound conditions, and CEL expressions.
 *
 * CONSTRAINTS:
 * - Type-safe condition evaluation
 * - Support for nested conditions
 * - CEL expression compatibility (future)
 */

import { useCallback, useMemo } from 'react';
import type {
  Condition,
  SimpleCondition,
  CompoundCondition,
  ConditionOperator,
} from './types';

// ============================================================================
// TYPES
// ============================================================================

type ConditionEvaluator = (condition: Condition) => boolean;

// ============================================================================
// OPERATOR IMPLEMENTATIONS
// ============================================================================

/**
 * Get nested value from object by path
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Compare two values for equality (deep comparison for objects/arrays)
 */
function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, i) => isEqual(val, b[i]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a as object);
    const bKeys = Object.keys(b as object);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) =>
      isEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
    );
  }

  return false;
}

/**
 * Evaluate a condition operator
 */
function evaluateOperator(
  operator: ConditionOperator,
  fieldValue: unknown,
  conditionValue: unknown
): boolean {
  switch (operator) {
    case 'eq':
      return isEqual(fieldValue, conditionValue);

    case 'ne':
      return !isEqual(fieldValue, conditionValue);

    case 'gt':
      if (typeof fieldValue === 'number' && typeof conditionValue === 'number') {
        return fieldValue > conditionValue;
      }
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue > conditionValue;
      }
      if (fieldValue instanceof Date && conditionValue instanceof Date) {
        return fieldValue > conditionValue;
      }
      return false;

    case 'gte':
      if (typeof fieldValue === 'number' && typeof conditionValue === 'number') {
        return fieldValue >= conditionValue;
      }
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue >= conditionValue;
      }
      if (fieldValue instanceof Date && conditionValue instanceof Date) {
        return fieldValue >= conditionValue;
      }
      return false;

    case 'lt':
      if (typeof fieldValue === 'number' && typeof conditionValue === 'number') {
        return fieldValue < conditionValue;
      }
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue < conditionValue;
      }
      if (fieldValue instanceof Date && conditionValue instanceof Date) {
        return fieldValue < conditionValue;
      }
      return false;

    case 'lte':
      if (typeof fieldValue === 'number' && typeof conditionValue === 'number') {
        return fieldValue <= conditionValue;
      }
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue <= conditionValue;
      }
      if (fieldValue instanceof Date && conditionValue instanceof Date) {
        return fieldValue <= conditionValue;
      }
      return false;

    case 'in':
      if (Array.isArray(conditionValue)) {
        return conditionValue.some((v) => isEqual(fieldValue, v));
      }
      return false;

    case 'notIn':
      if (Array.isArray(conditionValue)) {
        return !conditionValue.some((v) => isEqual(fieldValue, v));
      }
      return true;

    case 'contains':
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue.includes(conditionValue);
      }
      if (Array.isArray(fieldValue)) {
        return fieldValue.some((v) => isEqual(v, conditionValue));
      }
      return false;

    case 'startsWith':
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue.startsWith(conditionValue);
      }
      return false;

    case 'endsWith':
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue.endsWith(conditionValue);
      }
      return false;

    case 'matches':
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        try {
          const regex = new RegExp(conditionValue);
          return regex.test(fieldValue);
        } catch {
          return false;
        }
      }
      return false;

    case 'isEmpty':
      if (fieldValue === null || fieldValue === undefined) return true;
      if (typeof fieldValue === 'string') return fieldValue.length === 0;
      if (Array.isArray(fieldValue)) return fieldValue.length === 0;
      if (typeof fieldValue === 'object') return Object.keys(fieldValue).length === 0;
      return false;

    case 'isNotEmpty':
      if (fieldValue === null || fieldValue === undefined) return false;
      if (typeof fieldValue === 'string') return fieldValue.length > 0;
      if (Array.isArray(fieldValue)) return fieldValue.length > 0;
      if (typeof fieldValue === 'object') return Object.keys(fieldValue).length > 0;
      return true;

    case 'isNull':
      return fieldValue === null || fieldValue === undefined;

    case 'isNotNull':
      return fieldValue !== null && fieldValue !== undefined;

    default:
      console.warn(`Unknown operator: ${operator}`);
      return false;
  }
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if condition is a simple condition
 */
function isSimpleCondition(condition: Condition): condition is SimpleCondition {
  return typeof condition === 'object' && 'field' in condition && 'operator' in condition;
}

/**
 * Check if condition is a compound condition
 */
function isCompoundCondition(condition: Condition): condition is CompoundCondition {
  return (
    typeof condition === 'object' &&
    ('and' in condition || 'or' in condition || 'not' in condition)
  );
}

/**
 * Check if condition is a CEL expression string
 */
function isCELExpression(condition: Condition): condition is string {
  return typeof condition === 'string';
}

// ============================================================================
// EVALUATOR FACTORY
// ============================================================================

/**
 * Create a condition evaluator with form values context
 */
function createConditionEvaluator(
  values: Record<string, unknown>
): ConditionEvaluator {
  /**
   * Evaluate a simple condition
   */
  function evaluateSimple(condition: SimpleCondition): boolean {
    const fieldValue = getNestedValue(values, condition.field);
    return evaluateOperator(condition.operator, fieldValue, condition.value);
  }

  /**
   * Evaluate a compound condition (AND/OR/NOT)
   */
  function evaluateCompound(condition: CompoundCondition): boolean {
    if (condition.and) {
      return condition.and.every((c) => evaluate(c));
    }

    if (condition.or) {
      return condition.or.some((c) => evaluate(c));
    }

    if (condition.not) {
      return !evaluate(condition.not);
    }

    return true;
  }

  /**
   * Evaluate a CEL expression
   * TODO: Implement proper CEL parser
   */
  function evaluateCEL(expression: string): boolean {
    try {
      // For now, use a simple JavaScript-like evaluation
      // This is a placeholder until we implement proper CEL support

      // Replace field references with actual values
      let processedExpression = expression;

      // Match field references like `fieldName` or `field.nested`
      const fieldPattern = /\b([a-zA-Z_][a-zA-Z0-9_.]*)\b/g;
      const matches = expression.match(fieldPattern) || [];

      // Filter out JavaScript keywords
      const jsKeywords = new Set([
        'true', 'false', 'null', 'undefined',
        'if', 'else', 'return', 'function',
        'const', 'let', 'var', 'new', 'this',
        'typeof', 'instanceof', 'in', 'of',
        'and', 'or', 'not',
      ]);

      for (const match of matches) {
        if (!jsKeywords.has(match)) {
          const value = getNestedValue(values, match);
          const serialized = JSON.stringify(value);
          processedExpression = processedExpression.replace(
            new RegExp(`\\b${match}\\b`, 'g'),
            serialized
          );
        }
      }

      // Replace CEL operators with JavaScript operators
      processedExpression = processedExpression
        .replace(/\band\b/g, '&&')
        .replace(/\bor\b/g, '||')
        .replace(/\bnot\b/g, '!');

      // Evaluate the expression
      const fn = new Function(`return ${processedExpression}`);
      return Boolean(fn());
    } catch (error) {
      console.warn('CEL evaluation error:', error, expression);
      return false;
    }
  }

  /**
   * Main evaluation function
   */
  function evaluate(condition: Condition): boolean {
    if (isCELExpression(condition)) {
      return evaluateCEL(condition);
    }

    if (isSimpleCondition(condition)) {
      return evaluateSimple(condition);
    }

    if (isCompoundCondition(condition)) {
      return evaluateCompound(condition);
    }

    console.warn('Unknown condition type:', condition);
    return true;
  }

  return evaluate;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for condition evaluation with form values
 */
export function useConditionEvaluator(
  values: Record<string, unknown>
): ConditionEvaluator {
  const evaluator = useMemo(() => createConditionEvaluator(values), [values]);
  return evaluator;
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Build a simple condition
 */
export function condition(
  field: string,
  operator: ConditionOperator,
  value?: unknown
): SimpleCondition {
  return { field, operator, value };
}

/**
 * Build an AND compound condition
 */
export function and(...conditions: Condition[]): CompoundCondition {
  return { and: conditions };
}

/**
 * Build an OR compound condition
 */
export function or(...conditions: Condition[]): CompoundCondition {
  return { or: conditions };
}

/**
 * Build a NOT compound condition
 */
export function not(condition: Condition): CompoundCondition {
  return { not: condition };
}

/**
 * Common condition shortcuts
 */
export const conditions = {
  /** Field equals value */
  eq: (field: string, value: unknown) => condition(field, 'eq', value),

  /** Field does not equal value */
  ne: (field: string, value: unknown) => condition(field, 'ne', value),

  /** Field is greater than value */
  gt: (field: string, value: number) => condition(field, 'gt', value),

  /** Field is greater than or equal to value */
  gte: (field: string, value: number) => condition(field, 'gte', value),

  /** Field is less than value */
  lt: (field: string, value: number) => condition(field, 'lt', value),

  /** Field is less than or equal to value */
  lte: (field: string, value: number) => condition(field, 'lte', value),

  /** Field is in array of values */
  in: (field: string, values: unknown[]) => condition(field, 'in', values),

  /** Field is not in array of values */
  notIn: (field: string, values: unknown[]) => condition(field, 'notIn', values),

  /** Field contains value */
  contains: (field: string, value: string) => condition(field, 'contains', value),

  /** Field starts with value */
  startsWith: (field: string, value: string) => condition(field, 'startsWith', value),

  /** Field ends with value */
  endsWith: (field: string, value: string) => condition(field, 'endsWith', value),

  /** Field matches regex pattern */
  matches: (field: string, pattern: string) => condition(field, 'matches', pattern),

  /** Field is empty (null, undefined, empty string, empty array) */
  isEmpty: (field: string) => condition(field, 'isEmpty'),

  /** Field is not empty */
  isNotEmpty: (field: string) => condition(field, 'isNotEmpty'),

  /** Field is null or undefined */
  isNull: (field: string) => condition(field, 'isNull'),

  /** Field is not null */
  isNotNull: (field: string) => condition(field, 'isNotNull'),

  /** Field is truthy */
  isTrue: (field: string) => condition(field, 'eq', true),

  /** Field is falsy */
  isFalse: (field: string) => condition(field, 'eq', false),
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
  createConditionEvaluator,
  evaluateOperator,
  isSimpleCondition,
  isCompoundCondition,
  isCELExpression,
  getNestedValue,
};

export type { ConditionEvaluator };
